import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Trash2, Edit2, Check, X, ArrowRight } from 'lucide-react';
import { useSelector } from '@xstate/store/react';
import { boardsStore, generateBoardId } from '../stores/boardsStore';
import type { BoardDefinition } from '../types';
import { getEmployeesArray } from '../employees/employeesStore';
import { PageContainer, PageHeader } from '../components/PageLayout';
import { QtplanLogo } from '../components/QtplanLogo';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function EmptyState({ onCreateBoard }: { onCreateBoard: (name: string) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onCreateBoard(name.trim());
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-2rem)]">
      <div className="max-w-sm w-full mx-auto px-6">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
              <QtplanLogo size={18} className="text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900 tracking-tight">qtplan</span>
          </div>
          <p className="text-sm text-gray-400">
            {t('boards.emptyHint')}
          </p>
        </div>

        {/* Create form */}
        <div>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('boards.boardNamePlaceholder')}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300"
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {t('boards.createButton')}
            <ArrowRight size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function BoardsPage() {
  useDocumentTitle('Boards – qtplan');
  const { t } = useTranslation();
  const boards = useSelector(boardsStore, (state) => state.context.boards);
  const activeBoardId = useSelector(boardsStore, (state) => state.context.activeBoardId);
  const navigate = useNavigate();

  const [showAddRow, setShowAddRow] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showAddRow) {
      addInputRef.current?.focus();
    }
  }, [showAddRow]);

  const handleCreateBoard = (boardName?: string) => {
    const finalName = boardName ?? newBoardName;
    if (!finalName.trim()) return;

    const now = new Date().toISOString();
    const allEmployeeIds = getEmployeesArray().map(e => e.id);
    const boardId = generateBoardId(finalName);

    const board: BoardDefinition = {
      id: boardId,
      name: finalName.trim(),
      startDate: getTodayISO(),
      createdAt: now,
      updatedAt: now,
      employeeIds: allEmployeeIds,
    };

    boardsStore.send({ type: 'board.create', board });
    setNewBoardName('');
    setShowAddRow(false);
    navigate({ to: '/boards/$boardId/roadmap', params: { boardId } });
  };

  const handleCloseAddRow = () => {
    setShowAddRow(false);
    setNewBoardName('');
  };

  const handleDeleteBoard = (boardId: string) => {
    if (!confirm(t('boards.deleteConfirm'))) return;
    boardsStore.send({ type: 'board.delete', boardId });
  };

  const handleRenameBoard = (boardId: string) => {
    if (!editName.trim()) return;
    boardsStore.send({
      type: 'board.update',
      boardId,
      updates: { name: editName.trim() },
    });
    setEditingBoardId(null);
  };

  const handleOpenBoard = (boardId: string) => {
    boardsStore.send({ type: 'board.setActive', boardId });
    navigate({ to: '/boards/$boardId/roadmap', params: { boardId } });
  };

  // Show welcome screen when no boards exist
  if (boards.length === 0) {
    return <EmptyState onCreateBoard={(name) => handleCreateBoard(name)} />;
  }

  return (
    <PageContainer wide>
      <PageHeader
        title={t('boards.title')}
        buttonLabel={t('boards.createButton')}
        onButtonClick={() => setShowAddRow(true)}
      />

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="divide-y divide-gray-100">
          {showAddRow && (
            <div className="px-3 py-2 flex items-center gap-2">
              <input
                ref={addInputRef}
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder={t('boards.boardNameInputPlaceholder')}
                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs text-gray-900 bg-white focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateBoard();
                  if (e.key === 'Escape') handleCloseAddRow();
                }}
              />
              <button
                onClick={handleCloseAddRow}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {boards.map((board) => (
            <div
              key={board.id}
              className={`px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer ${
                board.id === activeBoardId ? 'bg-gray-50' : ''
              }`}
              onClick={() => handleOpenBoard(board.id)}
            >
              <div className="flex-1 min-w-0">
                {editingBoardId === board.id ? (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="px-2 py-1 border border-gray-200 rounded text-xs text-gray-900 bg-white"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameBoard(board.id);
                        if (e.key === 'Escape') setEditingBoardId(null);
                      }}
                    />
                    <button
                      onClick={() => handleRenameBoard(board.id)}
                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => setEditingBoardId(null)}
                      className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-xs font-medium text-gray-900">{board.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Start: {board.startDate} &middot; {t('boards.employeeCount', { count: board.employeeIds.length })}
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1 ml-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => {
                    setEditingBoardId(board.id);
                    setEditName(board.name);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title={t('common.rename')}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDeleteBoard(board.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title={t('common.delete')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageContainer>
  );
}
